import React, { useState, useEffect } from 'react';
import { X, Copy, Check, RefreshCw, AlertCircle, Link as LinkIcon, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import apiClient from '../../services/apiClient';

const AccessTokenModal = ({ student, onClose }) => {
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedType, setCopiedType] = useState(null); // 'parent' or 'admin' or null
  const [showUrl, setShowUrl] = useState(false);

  const studentId = student?.id || student?.student_id;
  const studentName = student?.name || student?.student_name || '同学';

  const getAccessUrl = (token, isAdmin = false) => {
    const base = import.meta.env.VITE_PARENT_APP_URL || 'https://i.sumarts.cn';
    return isAdmin ? `${base}/${token}.html?admin_view=true` : `${base}/${token}.html`;
  };

  const fetchToken = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(`/api/admin/access-token/${studentId}`);
      const data = res.data?.data || res.data;
      
      if (data && data.token) {
        setTokenInfo(data);
      } else {
        // 如果没有令牌，则自动重置/生成一个
        const createRes = await apiClient.post(`/api/admin/access-token/${studentId}/reset`);
        setTokenInfo(createRes.data?.data || createRes.data);
      }
    } catch (err) {
      console.error('Fetch token failed:', err);
      // 失败重试逻辑：尝试直接重置生成
      try {
        const createRes = await apiClient.post(`/api/admin/access-token/${studentId}/reset`);
        setTokenInfo(createRes.data?.data || createRes.data);
      } catch (e2) {
        setError('获取访问链接失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (url, type) => {
    const doCopy = (text) => {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          setCopiedType(type);
          setTimeout(() => {
            setCopiedType(null);
          }, 1500);
        }).catch(() => fallbackCopy(text));
      } else {
        fallbackCopy(text);
      }
    };

    const fallbackCopy = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      document.body.appendChild(textArea);
      
      // 针对 iOS 的额外适配
      textArea.readOnly = true; 
      textArea.focus();
      textArea.select();
      if (textArea.setSelectionRange) {
        textArea.setSelectionRange(0, text.length);
      }

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setCopiedType(type);
          setTimeout(() => {
            setCopiedType(null);
          }, 1500);
        } else {
          throw new Error('execCommand copy failed');
        }
      } catch (err) {
        console.error('Fallback copy failed:', err);
        alert('复制失败，请手动长按链接复制');
      }
      document.body.removeChild(textArea);
    };

    doCopy(url);
  };

  useEffect(() => {
    if (studentId) fetchToken();
  }, [studentId]);

  if (!student) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col transform transition-all animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
            <LinkIcon size={16} className="text-blue-500" />
            快速分享
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-200/60 rounded-full transition-colors text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 flex flex-col items-center text-center">
          {/* 学员头像与信息 */}
          <div className="w-14 h-14 bg-gradient-to-tr from-blue-100 to-indigo-50 rounded-2xl flex items-center justify-center text-blue-600 font-black text-xl mb-2 shadow-inner">
            {studentName.charAt(0)}
          </div>
          
          <h2 className="text-base font-bold text-gray-900">{studentName}</h2>
          <p className="text-xs text-gray-400 mb-4">请选择需要的快捷访问链接</p>

          {error ? (
            <div className="w-full bg-red-50 text-red-500 p-3 rounded-xl text-xs flex items-center gap-2 mb-4">
              <AlertCircle size={14} />
              {error}
            </div>
          ) : loading ? (
            <div className="py-8 flex flex-col items-center gap-2">
              <RefreshCw size={24} className="animate-spin text-blue-500 opacity-30" />
              <span className="text-xs text-gray-400">正在生成安全链接...</span>
            </div>
          ) : (
            <div className="w-full space-y-3">
              {/* 卡片 1: 家长访问链接 */}
              <div className="bg-gray-50/80 rounded-2xl p-3.5 border border-gray-100 text-left transition-all hover:border-blue-100">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span className="text-xs font-bold text-gray-800">家长访问链接</span>
                  </div>
                  <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">统计阅读日志</span>
                </div>
                
                <p className="text-[11px] text-gray-400 mb-3 leading-relaxed">
                  用于发送给家长，家长免密登录并自动计入访问统计。
                </p>

                {/* 操作按钮组 */}
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => handleCopy(getAccessUrl(tokenInfo.token, false), 'parent')}
                    className={`flex-1 h-9 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-[0.98] shadow-sm ${
                      copiedType === 'parent'
                        ? 'bg-green-500 text-white'
                        : 'bg-[#3182CE] hover:bg-blue-600 text-white shadow-blue-100'
                    }`}
                  >
                    {copiedType === 'parent' ? <Check size={14} /> : <Copy size={14} />}
                    {copiedType === 'parent' ? '链接已复制' : '复制家长链接'}
                  </button>

                  <a
                    href={getAccessUrl(tokenInfo.token, false)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 px-3.5 rounded-xl text-xs font-medium bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 flex items-center justify-center gap-1 transition-all active:scale-[0.98] shadow-sm"
                    title="在浏览器中直接打开访问"
                  >
                    <ExternalLink size={13} className="text-gray-500" />
                    <span>访问</span>
                  </a>
                </div>

                {/* 隐藏的 URL 展开 */}
                {showUrl && (
                  <div className="mt-2.5 p-2 bg-white rounded-lg border border-gray-100 break-all font-mono text-[10px] text-gray-400 select-all leading-normal animate-in fade-in duration-200">
                    {getAccessUrl(tokenInfo.token, false)}
                  </div>
                )}
              </div>

              {/* 卡片 2: 管理员专属链接 */}
              <div className="bg-gray-50/80 rounded-2xl p-3.5 border border-gray-100 text-left transition-all hover:border-indigo-100">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                    <span className="text-xs font-bold text-gray-800">管理员专属链接</span>
                  </div>
                  <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">免记录日志</span>
                </div>
                
                <p className="text-[11px] text-gray-400 mb-3 leading-relaxed">
                  专供老师或管理员巡检，不会污染家长的阅读统计数据。
                </p>

                {/* 操作按钮组 */}
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => handleCopy(getAccessUrl(tokenInfo.token, true), 'admin')}
                    className={`flex-1 h-9 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-[0.98] shadow-sm ${
                      copiedType === 'admin'
                        ? 'bg-green-500 text-white'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-100'
                    }`}
                  >
                    {copiedType === 'admin' ? <Check size={14} /> : <Copy size={14} />}
                    {copiedType === 'admin' ? '链接已复制' : '复制管理链接'}
                  </button>

                  <a
                    href={getAccessUrl(tokenInfo.token, true)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 px-3.5 rounded-xl text-xs font-medium bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 flex items-center justify-center gap-1 transition-all active:scale-[0.98] shadow-sm"
                    title="在浏览器中直接预览检查"
                  >
                    <ExternalLink size={13} className="text-gray-500" />
                    <span>预览</span>
                  </a>
                </div>

                {/* 隐藏的 URL 展开 */}
                {showUrl && (
                  <div className="mt-2.5 p-2 bg-white rounded-lg border border-gray-100 break-all font-mono text-[10px] text-indigo-500 select-all leading-normal animate-in fade-in duration-200">
                    {getAccessUrl(tokenInfo.token, true)}
                  </div>
                )}
              </div>

              {/* 展开/收起完整网址切换按钮 */}
              <div className="pt-1 flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowUrl(!showUrl)}
                  className="text-[11px] text-gray-400 hover:text-gray-600 flex items-center gap-1 py-1 px-2.5 rounded-lg transition-colors hover:bg-gray-50"
                >
                  <span>{showUrl ? '收起完整网址' : '查看完整网址'}</span>
                  {showUrl ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessTokenModal;
